/**
 * 기관(테넌트) 예산 설정 — 편성요구·조정·현액·예산서 공통
 * 키: budgetTenantSettings|{tenantCode}
 *
 * finalBudgetMode (택1):
 *   - sumDelta  : 확정 차수(본·추경·성립전·간주) 증감 합 = 최종 · GNK 기본
 *   - lastRound : 마지막 확정 차수 절대액 = 최종 (직전 전체 불러와 수정)
 * priorYearBasis:
 *   - mainOnly  : 전년도 본예산(100)만
 *   - yearFinal : 전년도 최종 (`finalBudgetMode`와 동일 규칙)
 *
 * SSOT: D:\295\erp\BD\design\08_차수증감_기정최종_전년옵션.md
 *
 * - isBdApiEnabled(): BdTenantBudgetSettings API (표시명 등 여분 항목은 extraJson)
 * - 그 외: localStorage PoC
 * - 동기 getter는 load 가 채운 메모리 캐시를 읽는다.
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet } from 'services/commonQuery';

export const BUDGET_TENANT_SETTINGS_PREFIX = 'budgetTenantSettings';

const settingsCache = new Map();

/** @typedef {'mainOnly' | 'yearFinal'} PriorYearBasis */
/** @typedef {'sumDelta' | 'lastRound'} FinalBudgetMode */

export const PRIOR_YEAR_BASIS = {
  MAIN_ONLY: 'mainOnly',
  YEAR_FINAL: 'yearFinal',
};

export const PRIOR_YEAR_BASIS_OPTIONS = [
  { value: PRIOR_YEAR_BASIS.MAIN_ONLY, label: '전년 본예산만' },
  { value: PRIOR_YEAR_BASIS.YEAR_FINAL, label: '전년 최종' },
];

export const FINAL_BUDGET_MODE = {
  SUM_DELTA: 'sumDelta',
  LAST_ROUND: 'lastRound',
};

export const FINAL_BUDGET_MODE_OPTIONS = [
  {
    value: FINAL_BUDGET_MODE.SUM_DELTA,
    label: '합산(sumDelta)',
    title:
      '본·추경·성립전·간주 증감 합=최종 · 추경/성립전/간주는 증감 입력 · 현액은 성립전→추경 흡수·간주 별도',
  },
  {
    value: FINAL_BUDGET_MODE.LAST_ROUND,
    label: '최종차수(lastRound)',
    title:
      '마지막 확정 차수(성립전·간주 포함) 절대액=최종 · 직전 차수 전체 불러와 수정',
  },
];

/** GNK 등 합산 모드 기본 기관 */
const SUM_DELTA_DEFAULT_TENANTS = new Set(['GNK']);

export function budgetTenantSettingsKey(tenantCode) {
  return [BUDGET_TENANT_SETTINGS_PREFIX, tenantCode || ''].join('|');
}

function defaultFinalBudgetMode(tenantCode) {
  const t = String(tenantCode || '').trim().toUpperCase();
  if (SUM_DELTA_DEFAULT_TENANTS.has(t)) return FINAL_BUDGET_MODE.SUM_DELTA;
  return FINAL_BUDGET_MODE.SUM_DELTA;
}

function normalizeFinalBudgetMode(value, tenantCode) {
  if (value === FINAL_BUDGET_MODE.LAST_ROUND) return FINAL_BUDGET_MODE.LAST_ROUND;
  if (value === FINAL_BUDGET_MODE.SUM_DELTA) return FINAL_BUDGET_MODE.SUM_DELTA;
  return defaultFinalBudgetMode(tenantCode);
}

function defaultSettings(tenantCode) {
  return {
    /** @type {PriorYearBasis} */
    priorYearBasis: PRIOR_YEAR_BASIS.MAIN_ONLY,
    /** @type {FinalBudgetMode} */
    finalBudgetMode: defaultFinalBudgetMode(tenantCode),
    /** 표시명 (예산차수등록 UI와 동일) */
    spare1Label: '성립전',
    spare2Label: '간주예산',
  };
}

function normalizeSettings(data, tenantCode) {
  const defaults = defaultSettings(tenantCode);
  const src = data && typeof data === 'object' ? data : {};
  return {
    ...defaults,
    ...src,
    priorYearBasis:
      src.priorYearBasis === PRIOR_YEAR_BASIS.YEAR_FINAL
        ? PRIOR_YEAR_BASIS.YEAR_FINAL
        : PRIOR_YEAR_BASIS.MAIN_ONLY,
    finalBudgetMode: normalizeFinalBudgetMode(src.finalBudgetMode, tenantCode),
    spare1Label: String(src.spare1Label || defaults.spare1Label),
    spare2Label: String(src.spare2Label || defaults.spare2Label),
  };
}

function readSettingsFromStorage(tenantCode) {
  try {
    const raw = localStorage.getItem(budgetTenantSettingsKey(tenantCode));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** API 행 → FE 설정 (표시명은 extra_json) */
function mapApiRowToSettings(row, tenantCode) {
  let extra = {};
  try {
    if (row?.extraJson) extra = JSON.parse(row.extraJson) || {};
  } catch {
    extra = {};
  }
  return normalizeSettings(
    {
      ...extra,
      priorYearBasis: row?.priorYearBasis,
      finalBudgetMode: row?.finalBudgetMode,
      version: row?.version,
    },
    tenantCode
  );
}

/** 동기 getter용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedBudgetTenantSettings(tenantCode) {
  const tc = String(tenantCode || '').trim();
  const cached = cacheGet(settingsCache, tc);
  if (cached && !Array.isArray(cached)) return cached;
  if (isBdApiEnabled()) return normalizeSettings(null, tenantCode);
  const next = normalizeSettings(readSettingsFromStorage(tenantCode), tenantCode);
  settingsCache.set(tc, next);
  return next;
}

export async function loadBudgetTenantSettings(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdTenantBudgetSettings.selectSettings', { tenantCode: tc });
    const next = rows.length
      ? mapApiRowToSettings(rows[0], tenantCode)
      : normalizeSettings(null, tenantCode);
    settingsCache.set(tc, next);
    return next;
  }
  const next = normalizeSettings(readSettingsFromStorage(tenantCode), tenantCode);
  settingsCache.set(tc, next);
  return next;
}

export async function saveBudgetTenantSettings(tenantCode, patch = {}) {
  const tc = String(tenantCode || '').trim();
  const prev = isBdApiEnabled()
    ? await loadBudgetTenantSettings(tenantCode)
    : getCachedBudgetTenantSettings(tenantCode);
  const next = normalizeSettings(
    { ...prev, ...(patch && typeof patch === 'object' ? patch : {}) },
    tenantCode
  );
  settingsCache.set(tc, next);

  if (isBdApiEnabled()) {
    const { priorYearBasis, finalBudgetMode, ...extra } = next;
    delete extra.savedAt;
    delete extra.version;
    const rows = await commonSelect('BdTenantBudgetSettings.selectSettings', { tenantCode: tc });
    await commonSaveList(
      [
        'BdTenantBudgetSettings.insertSettings',
        'BdTenantBudgetSettings.updateSettings',
        'BdTenantBudgetSettings.updateSettings',
      ],
      [
        {
          priorYearBasis,
          finalBudgetMode,
          extraJson: JSON.stringify(extra),
          version: rows[0]?.version ?? next.version,
          mod: rows.length ? 'U' : 'A',
        },
      ],
      { tenantCode: tc }
    );
    return next;
  }

  localStorage.setItem(
    budgetTenantSettingsKey(tenantCode),
    JSON.stringify({ ...next, savedAt: new Date().toISOString() })
  );
  return next;
}

/** @returns {PriorYearBasis} */
export function getPriorYearBasis(tenantCode) {
  return getCachedBudgetTenantSettings(tenantCode).priorYearBasis;
}

export async function setPriorYearBasis(tenantCode, basis) {
  return saveBudgetTenantSettings(tenantCode, {
    priorYearBasis:
      basis === PRIOR_YEAR_BASIS.YEAR_FINAL
        ? PRIOR_YEAR_BASIS.YEAR_FINAL
        : PRIOR_YEAR_BASIS.MAIN_ONLY,
  });
}

/** @returns {FinalBudgetMode} */
export function getFinalBudgetMode(tenantCode) {
  return getCachedBudgetTenantSettings(tenantCode).finalBudgetMode;
}

export async function setFinalBudgetMode(tenantCode, mode) {
  return saveBudgetTenantSettings(tenantCode, {
    finalBudgetMode: normalizeFinalBudgetMode(mode, tenantCode),
  });
}

export function isSumDeltaMode(tenantCode) {
  return getFinalBudgetMode(tenantCode) === FINAL_BUDGET_MODE.SUM_DELTA;
}

export function isLastRoundMode(tenantCode) {
  return getFinalBudgetMode(tenantCode) === FINAL_BUDGET_MODE.LAST_ROUND;
}
